<script setup>
/* global axios */
// Box "Agendar Atendimento" — cria um agendamento (módulo Atendimentos) vinculado
// à conversa: acha/cadastra o paciente pelo telefone do contato, escolhe serviço +
// profissional + data/hora e grava. Porta a view "Agendar Atendimento" do widget
// original (SCRIPT 2 do dashboard injetado).
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
});

const { t, locale } = useI18n();
const dtLocale = computed(() => locale.value.replace('_', '-'));
const route = useRoute();
const accountId = route.params.accountId;
const base = `/api/v1/accounts/${accountId}/kanban`;

const currentChat = useMapGetter('getSelectedChat');
const contactName = computed(() => currentChat.value?.meta?.sender?.name || '');
const contactPhone = computed(
  () => currentChat.value?.meta?.sender?.phone_number || ''
);
const contactId = computed(() => currentChat.value?.meta?.sender?.id || null);

const loading = ref(true);
const saving = ref(false);
const services = ref([]);
const practitioners = ref([]);
const patient = ref(null);
const patientStatus = ref('loading'); // loading | found | not_found

// formulário do agendamento
const serviceId = ref('');
const practitionerId = ref('');
const dateStr = ref('');
const time = ref('');
const slots = ref([]);
const slotsLoading = ref(false);
const notes = ref('');

// edição inline do paciente
const editing = ref(false);
const ep = ref({ name: '', phone: '', email: '', cpf: '', birth_date: '' });

const tomorrowDate = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const p = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// carrega os horários livres respeitando os dias/horário do profissional (backend AvailabilityService)
const loadSlots = async () => {
  time.value = '';
  if (!practitionerId.value || !serviceId.value || !dateStr.value) {
    slots.value = [];
    return;
  }
  slotsLoading.value = true;
  try {
    slots.value =
      (
        await axios.get(`${base}/appointments/availability`, {
          params: {
            practitioner_id: practitionerId.value,
            service_id: serviceId.value,
            date: dateStr.value,
          },
        })
      ).data.data || [];
  } catch (e) {
    slots.value = [];
  } finally {
    slotsLoading.value = false;
  }
};
watch([serviceId, practitionerId, dateStr], loadSlots);

const load = async () => {
  loading.value = true;
  patientStatus.value = 'loading';
  try {
    const [svc, prac] = await Promise.all([
      axios.get(`${base}/appointment_services`),
      axios.get(`${base}/practitioners`),
    ]);
    services.value = (svc.data.data || []).filter(s => s.is_active !== false);
    practitioners.value = (prac.data.data || []).filter(
      p => p.is_active !== false
    );

    const phone = (contactPhone.value || '').replace(/\D/g, '');
    if (phone) {
      const { data } = await axios.get(`${base}/patients/by_phone`, {
        params: { phone },
      });
      patient.value = data.data || null;
      patientStatus.value = patient.value ? 'found' : 'not_found';
    } else {
      patientStatus.value = 'not_found';
    }
  } catch (e) {
    patientStatus.value = 'not_found';
  }
  if (!dateStr.value) dateStr.value = tomorrowDate();
  loading.value = false;
};

const createPatient = async () => {
  if (!contactName.value || !contactPhone.value) {
    useAlert(t('KANBAN.appointmentBox.noContact'));
    return;
  }
  saving.value = true;
  try {
    // Vincula o cliente ao CONTATO da conversa (from_contact dedupa por contato e
    // adota cliente phone-only existente) — evita o mesmo contato virar vários
    // clientes ao agendar em dias diferentes. Sem contact_id cai no create simples.
    const { data } = contactId.value
      ? await axios.post(`${base}/patients/from_contact`, {
          contact_id: contactId.value,
        })
      : await axios.post(`${base}/patients`, {
          name: contactName.value,
          phone: contactPhone.value,
        });
    patient.value = data.data;
    patientStatus.value = 'found';
  } catch (e) {
    useAlert(e?.response?.data?.error || t('KANBAN.appointmentBox.error'));
  } finally {
    saving.value = false;
  }
};

const startEditPatient = () => {
  ep.value = {
    name: patient.value?.name || '',
    phone: patient.value?.phone || '',
    email: patient.value?.email || '',
    cpf: patient.value?.cpf || '',
    birth_date: patient.value?.birth_date
      ? patient.value.birth_date.slice(0, 10)
      : '',
  };
  editing.value = true;
};

const savePatientEdit = async () => {
  if (!ep.value.name.trim() || !ep.value.phone.trim()) {
    useAlert(t('KANBAN.appointmentBox.patientRequired'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.put(`${base}/patients/${patient.value.id}`, {
      name: ep.value.name.trim(),
      phone: ep.value.phone.trim(),
      email: ep.value.email.trim() || null,
      cpf: ep.value.cpf.trim() || null,
      birth_date: ep.value.birth_date || null,
    });
    patient.value = data.data;
    editing.value = false;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('KANBAN.appointmentBox.error'));
  } finally {
    saving.value = false;
  }
};

const created = ref(null);
const book = async () => {
  if (!serviceId.value) {
    useAlert(t('KANBAN.appointmentBox.selectService'));
    return;
  }
  if (!practitionerId.value) {
    useAlert(t('KANBAN.appointmentBox.selectPractitioner'));
    return;
  }
  if (!dateStr.value || !time.value) {
    useAlert(t('KANBAN.appointmentBox.selectDate'));
    return;
  }
  saving.value = true;
  try {
    const { data } = await axios.post(`${base}/appointments`, {
      patient_id: patient.value.id,
      practitioner_id: practitionerId.value,
      service_id: serviceId.value,
      appointment_at: new Date(
        `${dateStr.value}T${time.value}:00`
      ).toISOString(),
      notes: notes.value.trim() || null,
      chatwoot_conversation_id: Number(props.conversationId),
    });
    created.value = data.data;
  } catch (e) {
    useAlert(e?.response?.data?.error || t('KANBAN.appointmentBox.error'));
  } finally {
    saving.value = false;
  }
};

const reset = () => {
  created.value = null;
  serviceId.value = '';
  practitionerId.value = '';
  notes.value = '';
  dateStr.value = tomorrowDate();
  time.value = '';
  slots.value = [];
};

const fmt = iso => (iso ? new Date(iso).toLocaleString(dtLocale.value) : '');

watch(() => props.conversationId, load);
onMounted(load);
</script>

<template>
  <div class="flex flex-col gap-2.5 px-3.5 pt-2.5 pb-3.5">
    <div v-if="loading" class="text-xs text-n-slate-10">
      {{ t('KANBAN.appointmentBox.loading') }}
    </div>

    <!-- Sucesso -->
    <template v-else-if="created">
      <div
        class="text-sm rounded-lg p-2 bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
      >
        {{ t('KANBAN.appointmentBox.success') }}
      </div>
      <div
        class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg p-2 leading-relaxed"
      >
        <div>
          <strong>{{ t('KANBAN.appointmentBox.patient') }}:</strong>
          {{ created.patient_name }}
        </div>
        <div>
          <strong>{{ t('KANBAN.appointmentBox.service') }}:</strong>
          {{ created.service_name }}
        </div>
        <div>
          <strong>{{ t('KANBAN.appointmentBox.when') }}:</strong>
          {{ fmt(created.appointment_at) }}
        </div>
      </div>
      <button
        class="w-full px-3 py-2 text-sm font-medium rounded-lg border border-n-weak text-n-slate-11 bg-n-background hover:bg-n-alpha-1"
        @click="reset"
      >
        {{ t('KANBAN.appointmentBox.newOne') }}
      </button>
    </template>

    <template v-else>
      <!-- Edição do paciente -->
      <div
        v-if="editing"
        class="flex flex-col gap-2 pt-1.5 border-t border-dashed border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            t('KANBAN.appointmentBox.patientName')
          }}</label>
          <input
            v-model="ep.name"
            class="w-full box-border px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            t('KANBAN.appointmentBox.patientPhone')
          }}</label>
          <input
            v-model="ep.phone"
            class="w-full box-border px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex flex-col gap-1">
          <label class="text-xs font-medium text-n-slate-11">{{
            t('KANBAN.appointmentBox.patientEmail')
          }}</label>
          <input
            v-model="ep.email"
            class="w-full box-border px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />
        </div>
        <div class="flex gap-2">
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('KANBAN.appointmentBox.patientCpf')
            }}</label>
            <input
              v-model="ep.cpf"
              class="w-full box-border min-w-0 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
          <div class="flex flex-col gap-1 flex-1 min-w-0">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('KANBAN.appointmentBox.patientBirth')
            }}</label>
            <input
              v-model="ep.birth_date"
              type="date"
              class="w-full box-border min-w-0 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
            />
          </div>
        </div>
        <div class="flex gap-2 mt-0.5">
          <button
            class="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50"
            :disabled="saving"
            @click="savePatientEdit"
          >
            {{ t('KANBAN.appointmentBox.save') }}
          </button>
          <button
            class="px-3 py-2 text-sm rounded-lg border border-n-weak text-n-slate-11 bg-transparent hover:bg-n-alpha-1"
            @click="editing = false"
          >
            {{ t('KANBAN.appointmentBox.cancel') }}
          </button>
        </div>
      </div>

      <template v-else>
        <!-- Sem paciente: oferece cadastrar -->
        <div v-if="patientStatus === 'not_found'" class="flex flex-col gap-1.5">
          <div class="text-xs text-n-slate-10">
            {{ t('KANBAN.appointmentBox.notRegistered') }}
          </div>
          <div
            v-if="contactName || contactPhone"
            class="text-xs text-n-slate-11 bg-n-alpha-1 rounded-lg p-2"
          >
            <strong>{{ contactName || '—' }}</strong>
            · {{ contactPhone || '—' }}
          </div>
          <button
            class="w-full px-3 py-2 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50"
            :disabled="saving || !contactPhone"
            @click="createPatient"
          >
            {{ t('KANBAN.appointmentBox.registerAndContinue') }}
          </button>
        </div>

        <!-- Paciente encontrado: formulário do agendamento -->
        <template v-else-if="patientStatus === 'found' && patient">
          <div
            class="flex items-center gap-2 text-xs text-n-slate-11 bg-green-50 dark:bg-green-900/20 rounded-lg p-2"
          >
            <span class="min-w-0 truncate">
              <strong class="text-green-700 dark:text-green-300">{{
                patient.name
              }}</strong>
              · {{ patient.phone || '—' }}
            </span>
            <button
              class="ml-auto shrink-0 text-n-blue-11 hover:underline"
              @click="startEditPatient"
            >
              {{ t('KANBAN.appointmentBox.edit') }}
            </button>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('KANBAN.appointmentBox.serviceLabel')
            }}</label>
            <div class="relative">
              <select
                v-model="serviceId"
                class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              >
                <option value="">
                  {{ t('KANBAN.appointmentBox.selectService') }}
                </option>
                <option v-for="s in services" :key="s.id" :value="s.id">
                  {{ s.name }}
                </option>
              </select>
              <span
                class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
              />
            </div>
          </div>

          <div class="flex flex-col gap-1">
            <label class="text-xs font-medium text-n-slate-11">{{
              t('KANBAN.appointmentBox.practitionerLabel')
            }}</label>
            <div class="relative">
              <select
                v-model="practitionerId"
                class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              >
                <option value="">
                  {{ t('KANBAN.appointmentBox.selectPractitioner') }}
                </option>
                <option v-for="p in practitioners" :key="p.id" :value="p.id">
                  {{ p.name }}
                </option>
              </select>
              <span
                class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
              />
            </div>
          </div>

          <div class="flex gap-2">
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label class="text-xs font-medium text-n-slate-11">{{
                t('KANBAN.appointmentBox.dateLabel')
              }}</label>
              <input
                v-model="dateStr"
                type="date"
                class="w-full box-border px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
              />
            </div>
            <div class="flex flex-col gap-1 flex-1 min-w-0">
              <label class="text-xs font-medium text-n-slate-11">{{
                t('KANBAN.appointmentBox.timeLabel')
              }}</label>
              <div class="relative">
                <select
                  v-model="time"
                  :disabled="!practitionerId || !serviceId || !slots.length"
                  class="w-full box-border appearance-none !bg-none !mb-0 !h-auto pl-3 pr-8 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40 disabled:opacity-60"
                >
                  <option value="">
                    {{
                      slotsLoading
                        ? t('KANBAN.appointmentBox.loadingSlots')
                        : t('KANBAN.appointmentBox.selectTime')
                    }}
                  </option>
                  <option v-for="s in slots" :key="s.iso" :value="s.time">
                    {{ s.time }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down pointer-events-none absolute ltr:right-2.5 rtl:left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-n-slate-10"
                />
              </div>
            </div>
          </div>
          <p
            v-if="
              practitionerId &&
              serviceId &&
              dateStr &&
              !slotsLoading &&
              !slots.length
            "
            class="text-[0.6875rem] text-n-amber-11 -mt-0.5"
          >
            {{ t('KANBAN.appointmentBox.noSlots') }}
          </p>

          <textarea
            v-model="notes"
            rows="2"
            :placeholder="t('KANBAN.appointmentBox.notesPlaceholder')"
            class="w-full box-border px-3 py-2 mt-0.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/40"
          />

          <button
            class="w-full px-3 py-2 mt-0.5 text-sm font-medium rounded-lg bg-n-brand text-white hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="saving"
            @click="book"
          >
            {{
              saving
                ? t('KANBAN.appointmentBox.saving')
                : t('KANBAN.appointmentBox.book')
            }}
          </button>
        </template>
      </template>
    </template>
  </div>
</template>
